import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, MapPin, User, Heart, ShoppingCart, Menu, Search } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import { useWebsiteSettings } from '../../context/WebsiteSettingsContext';
import { useDataRefresh } from '../../context/DataRefreshContext';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import { categoryAPI } from '../../services/api';
import { trackFacebookEvent } from '../../utils/facebookPixel';
import { Category } from '../../types';
import { CategoryTreeNode, normalizeCategoryTree } from '../../utils/categoryTree';
import './Navbar.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { itemCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const { user, isAuthenticated, logout } = useAuth();
  const { settings } = useWebsiteSettings();
  const { refreshTrigger } = useDataRefresh();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState<CategoryTreeNode[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Dynamic announcement bar state
  const [, setAnnouncementText] = useState({ bn: '', en: '' });
  const [, setShowAnnouncement] = useState(true);

  // Dynamic values from settings
  const siteName = settings?.site_name || 'Eco Gallery';
  const logoUrl = settings?.logo_url;

  useEffect(() => {
    fetchCategories();
  }, [refreshTrigger.categories]);

  // Fetch dynamic announcement bar content
  useEffect(() => {
    const fetchHomepageContent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/homepage/`);
        if (response.ok) {
          const data = await response.json();
          setAnnouncementText({
            bn: data.announcement_text_bn || '',
            en: data.announcement_text_en || '',
          });
          setShowAnnouncement(data.is_announcement_active);
        }
      } catch (error) {
        console.error('Failed to fetch announcement:', error);
      }
    };
    fetchHomepageContent();
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showUserMenu]);

  const fetchCategories = async () => {
    try {
      const data = await categoryAPI.getCategories();
      setCategories(normalizeCategoryTree(data));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackFacebookEvent('Search', {
        search_string: searchQuery.trim(),
        content_type: 'product'
      });
      navigate(`/products?search=${searchQuery}`);
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const currentLang = i18n.language;
  const activeCategorySlug = new URLSearchParams(location.search).get('category');
  const categoryLabel = (category: Category) => currentLang === 'bn' ? category.name_bn : category.name_en;
  const hasActiveCategory = (category: CategoryTreeNode): boolean => (
    activeCategorySlug === category.slug ||
    category.children.some(child => hasActiveCategory(child))
  );

  const handleCategoryMouseEnter = (e: React.MouseEvent<HTMLLIElement>) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    if (rect.right + 260 > window.innerWidth) {
      el.classList.add('flyout-left');
    } else {
      el.classList.remove('flyout-left');
    }
  };

  const renderDesktopCategory = (category: CategoryTreeNode, depth = 0) => {
    const hasChildren = category.children.length > 0;
    return (
      <li
        key={category.id}
        className={`menu-category ${hasChildren ? 'has-submenu' : ''} ${depth > 0 ? 'submenu-category' : ''}`}
        onMouseEnter={hasChildren ? handleCategoryMouseEnter : undefined}
      >
        <Link
          to={`/products?category=${category.slug}`}
          className={`menu-title ${location.pathname === '/products' && hasActiveCategory(category) ? 'active' : ''}`}
        >
          <span>{categoryLabel(category)}</span>
          {hasChildren && depth === 0 && (
            <ChevronDown className="submenu-chevron" size={16} strokeWidth={2.5} />
          )}
          {hasChildren && depth > 0 && (
            <ChevronRight className="submenu-chevron-right" size={16} strokeWidth={2.5} />
          )}
        </Link>
        {hasChildren && (
          <ul
            className={`category-submenu category-submenu-level-${Math.min(depth + 1, 3)}`}
          >
            {category.children.map(child => renderDesktopCategory(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  const renderMobileCategory = (category: CategoryTreeNode) => {
    const hasChildren = category.children.length > 0;
    return (
      <li key={category.id} className="menu-category mobile-category-branch">
        <Link
          to={`/products?category=${category.slug}`}
          className="menu-title"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {categoryLabel(category)}
        </Link>
        {hasChildren && (
          <ul className="mobile-subcategory-list">
            {category.children.map(renderMobileCategory)}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      <header className="header">
        {/* MAIN HEADER */}
        <div className="header-main">
          <div className="container header-main-container">
            {/* Logo */}
            <div className="header-main-left">
              <Link to="/" className="header-logo">
                {logoUrl ? (
                  <img src={logoUrl} alt={siteName} className="logo-image" />
                ) : (
                  <span className="logo-text-fallback">{siteName}</span>
                )}
              </Link>
            </div>

            {/* Pill Search Bar */}
            <div className="header-search-container">
              <form onSubmit={handleSearch} className="ref-search-form">
                <input
                  type="search"
                  name="search"
                  className="ref-search-input"
                  placeholder={t('search.searchIn') || 'Search in...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="ref-search-btn" type="submit" aria-label="Search">
                  <Search size={19} strokeWidth={2} />
                </button>
              </form>
            </div>

            {/* 5 Action Items (Right) */}
            <div className="header-user-actions">
              {/* 1. Track Order */}
              <Link to="/track-order" className="ref-nav-action-item">
                <div className="ref-nav-icon-container">
                  <MapPin className="ref-nav-icon" size={24} strokeWidth={1.6} />
                </div>
                <span className="ref-nav-label">{t('nav.trackOrder') || 'Track Order'}</span>
              </Link>

              {/* 2. My Account */}
              {isAuthenticated ? (
                <div className="user-menu-container">
                  <button
                    className="ref-nav-action-item"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    aria-label="User Menu"
                  >
                    <div className="ref-nav-icon-container">
                      <User className="ref-nav-icon" size={24} strokeWidth={1.6} />
                    </div>
                    <span className="ref-nav-label">
                      {user?.full_name?.split(' ')[0] || t('nav.myAccount') || 'My Account'}
                    </span>
                  </button>

                  {showUserMenu && (
                    <div className="user-dropdown">
                      <Link to="/dashboard" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                        📊 {t('customer.dashboard') || 'Dashboard'}
                      </Link>
                      <Link to="/profile" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                        👤 {t('customer.profile') || 'Profile'}
                      </Link>
                      <Link to="/order-history" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                        📋 {t('customer.order_history') || 'Orders'}
                      </Link>
                      {user?.is_staff && (
                        <Link to="/admin" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                          ⚙️ {currentLang === 'bn' ? 'অ্যাডমিন' : 'Admin'}
                        </Link>
                      )}
                      <div className="dropdown-divider"></div>
                      <button className="dropdown-item logout-item" onClick={handleLogout}>
                        🚪 {t('auth.logout') || 'Logout'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="ref-nav-action-item">
                  <div className="ref-nav-icon-container">
                    <User className="ref-nav-icon" size={24} strokeWidth={1.6} />
                  </div>
                  <span className="ref-nav-label">{t('nav.myAccount') || 'My Account'}</span>
                </Link>
              )}

              {/* 3. Wishlist */}
              <Link to="/wishlist" className="ref-nav-action-item">
                <div className="ref-nav-icon-container">
                  <Heart className="ref-nav-icon" size={24} strokeWidth={1.6} />
                  {wishlistCount > 0 && <span className="ref-nav-badge">{wishlistCount}</span>}
                </div>
                <span className="ref-nav-label">{t('nav.wishlist') || 'Wishlist'}</span>
              </Link>

              {/* 4. Cart */}
              <Link to="/cart" className="ref-nav-action-item">
                <div className="ref-nav-icon-container">
                  <ShoppingCart className="ref-nav-icon" size={24} strokeWidth={1.6} />
                  {itemCount > 0 && <span className="ref-nav-badge">{itemCount}</span>}
                </div>
                <span className="ref-nav-label">{t('nav.cart') || 'Cart'}</span>
              </Link>

              {/* 5. More */}
              <button
                className="ref-nav-action-item"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="More Options"
              >
                <div className="ref-nav-icon-container">
                  <Menu className="ref-nav-icon" size={24} strokeWidth={1.6} />
                </div>
                <span className="ref-nav-label">{t('nav.more') || 'More'}</span>
              </button>

              {settings?.show_language_switcher !== false && (
                <div className="header-language-switcher-wrapper">
                  <LanguageSwitcher />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DESKTOP NAVIGATION */}
        <nav className="desktop-navigation-menu">
          <div className="container">
            <ul className="desktop-menu-category-list">
              <li className="menu-category">
                <Link
                  to="/products"
                  className={`menu-title ${location.pathname === '/products' && !activeCategorySlug ? 'active' : ''}`}
                >
                  {t('nav.products')}
                </Link>
              </li>

              {categories.map(category => renderDesktopCategory(category))}
            </ul>
          </div>
        </nav>

      </header>

      {/* MOBILE NAVIGATION MENU */}
      <nav className={`mobile-navigation-menu ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="menu-top">
          <h2 className="menu-title">{currentLang === 'bn' ? 'মেনু' : 'Menu'}</h2>
          <button className="menu-close-btn" onClick={() => setIsMobileMenuOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <ul className="mobile-menu-category-list">
          <li className="menu-category">
            <Link to="/" className="menu-title" onClick={() => setIsMobileMenuOpen(false)}>
              {t('nav.home')}
            </Link>
          </li>
          <li className="menu-category">
            <Link to="/products" className="menu-title" onClick={() => setIsMobileMenuOpen(false)}>
              {t('nav.products')}
            </Link>
          </li>
          {categories.map(renderMobileCategory)}
        </ul>

        {!isAuthenticated && (
          <div className="mobile-auth-buttons">
            <Link to="/login" className="btn btn-primary" onClick={() => setIsMobileMenuOpen(false)}>
              {t('auth.login')}
            </Link>
            <Link to="/register" className="btn btn-outline" onClick={() => setIsMobileMenuOpen(false)}>
              {t('auth.register')}
            </Link>
          </div>
        )}
      </nav>

      {/* MOBILE BOTTOM NAVIGATION */}
      <div className="mobile-bottom-navigation">
        <Link to="/cart" className="action-btn mobile-cart-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
          {itemCount > 0 && <span className="count">{itemCount}</span>}
        </Link>

        <a href={`tel:${settings?.support_phone}`} className="action-btn call-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </a>

        <Link to="/" className="action-btn home-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </Link>

        <a
          href={`https://wa.me/${settings?.whatsapp_number?.replace(/\+/g, '').replace(/[^0-9]/g, '')}`}
          className="action-btn whatsapp-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.03 0C5.439 0 .02 5.417.015 12.012c0 2.11.55 4.171 1.597 6.049L0 24l6.135-1.61a11.782 11.782 0 005.895 1.57h.005c6.59 0 12.009-5.417 12.014-12.012a11.78 11.78 0 00-3.515-8.498z" />
          </svg>
        </a>

        <Link to="/wishlist" className="action-btn mobile-wishlist-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          {wishlistCount > 0 && <span className="count">{wishlistCount}</span>}
        </Link>
      </div>

      {/* Overlay for mobile menu */}
      <div
        className={`overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>
    </>
  );
};

export default Navbar;
